import { Divider, Grid } from '@material-ui/core';
import { Form, FormikProvider, useFormik } from 'formik';
import React, { FC } from 'react';

import { Button } from '~/@components/Button';
import { FormikDateField, FormikNumberField, FormikTextField } from '~/@components/FormikFields';
import { LocalizableText } from '~/@components/LocalizableText';
import { Text } from '~/@components/Text';
import { DialogContent } from '~/@sochi-components/SochiDialog/DialogContent';
import { getProjectTimeSpentFormik } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/ProjectTimeSpentEditForm/ProjectTimeSpentFormik';
import { ProjectTimeSpentLicensePlateSelect } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/ProjectTimeSpentEditForm/ProjectTimeSpentLicensePlateSelect';
import { ProjectTimeSpentOrderSelect } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/ProjectTimeSpentEditForm/ProjectTimeSpentOrderSelect';
import { ProjectTimeSpentUserSelect } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/ProjectTimeSpentEditForm/ProjectTimeSpentUserSelect';
import { Project, ProjectTimeSpentRecord } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/types';
import { commonPrecisions } from '~/config/enum';
import i18n from '~/i18n';

type Props = {
    project: Project;
    timeSpent: ProjectTimeSpentRecord | null;
    onClose: (v: boolean) => void;
};

export const ProjectTimeSpentForm: FC<Props> = ({ project, timeSpent, onClose }) => {
    const formik = useFormik(getProjectTimeSpentFormik(project, timeSpent, () => onClose(true)));

    const onSave = () => {
        return formik.submitForm();
    };

    const onCancel = () => {
        formik.handleReset(null);
        onClose(false);
    };

    return (
        <DialogContent>
            <FormikProvider value={formik}>
                <Form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Text variant="TitleBold">
                                <LocalizableText
                                    code={
                                        timeSpent
                                            ? 'ProjectDocuments.timeSpent.updateTimeSpent'
                                            : 'ProjectDocuments.timeSpent.createTimeSpent'
                                    }
                                />
                            </Text>
                        </Grid>
                        <Grid item xs={6}>
                            <ProjectTimeSpentOrderSelect disabled={!!timeSpent} />
                        </Grid>
                        <Grid item xs={6}>
                            <ProjectTimeSpentUserSelect disabled={!!timeSpent} />
                        </Grid>
                        <Grid item xs={6}>
                            <FormikDateField
                                label={<LocalizableText code={'ProjectDocuments.timeSpent.date'} />}
                                name="date"
                                formik={formik}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormikDateField
                                label={<LocalizableText code={'ProjectDocuments.timeSpent.endDate'} />}
                                name="endDate"
                                formik={formik}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <FormikNumberField
                                label={<LocalizableText code={'ProjectDocuments.timeSpent.hoursSpent'} />}
                                name="hoursSpent"
                                precision={commonPrecisions.amount}
                                min={0}
                                formik={formik}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <ProjectTimeSpentLicensePlateSelect disabled={!formik.values.user || !!timeSpent} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormikTextField
                                label={<LocalizableText code={'ProjectDocuments.timeSpent.comment'} />}
                                name="comment"
                                formik={formik}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider />
                        </Grid>
                        <Grid container spacing={2} justify="space-between">
                            <Grid item>
                                <Button onClick={onCancel} variant="contained">
                                    {i18n.cancel}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    disabled={!formik.dirty || formik.isSubmitting}
                                    onClick={onSave}
                                    variant="contained"
                                    color="primary">
                                    {i18n.save}
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Form>
            </FormikProvider>
        </DialogContent>
    );
};
